/**
 * What a signed-in shopper has bought across Talab — every merchant, one
 * history. Each checkout creates one order per merchant, because each
 * merchant fulfils its own.
 */

export type PurchaseStatus = 'received' | 'progress' | 'completed' | 'cancelled'

export interface PurchaseLine {
  listingId: string
  title: string
  /** "Double patty · no pickles" / "100 ml" / "Size M · Sand" */
  summary: string
  image: string
  unitPrice: number
  qty: number
}

export interface PurchaseOrder {
  id: string
  merchant: string
  /** Epoch ms. */
  placedAt: number
  status: PurchaseStatus
  lines: PurchaseLine[]
  total: number
  payment: string
  /** One plain line on how it reaches the customer. */
  fulfilment: string
  /** Times each stage was reached, for the timeline. */
  progressAt?: number
  completedAt?: number
  cancelledAt?: number
  cancelReason?: string
}

export const PURCHASE_STATUS: Record<
  PurchaseStatus,
  { label: string; tone: string }
> = {
  received: {
    label: 'Order received',
    tone: 'bg-primary-indigo/10 text-primary-indigo',
  },
  progress: {
    label: 'In progress',
    tone: 'bg-amber-50 text-amber-700',
  },
  completed: {
    label: 'Completed',
    tone: 'bg-success/10 text-success',
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'bg-slate-100 text-slate-500',
  },
}

export const isCurrent = (o: PurchaseOrder) =>
  o.status === 'received' || o.status === 'progress'

const IMG = {
  burger:
    'https://cdn.magicpatterns.com/patterns/generated-images/b2a4db08-1036-4bc9-a23f-17ea02c09146.jpg',
  perfume:
    'https://cdn.magicpatterns.com/patterns/generated-images/d0a21aab-d070-413a-a2e5-b63ee8fe799e.jpg',
  shirt:
    'https://cdn.magicpatterns.com/patterns/generated-images/8e599889-5018-4ef0-a32a-cf1623b2086c.jpg',
  sofa: 'https://cdn.magicpatterns.com/patterns/generated-images/26a963c1-853b-4408-91a8-ef3e9520b15b.jpg',
  prado:
    'https://cdn.magicpatterns.com/patterns/generated-images/daa13e0d-60d6-4ee9-907b-f972b4ca1779.jpg',
}

const MIN = 60_000
const HOUR = 60 * MIN
const DAY = 24 * HOUR

/** Seeded relative to now so "Today" and "Yesterday" always read true. */
export function seedPurchases(now = Date.now()): PurchaseOrder[] {
  return [
    {
      id: 'TL-20418',
      merchant: 'Smash Society',
      placedAt: now - 22 * MIN,
      progressAt: now - 18 * MIN,
      status: 'progress',
      lines: [
        {
          listingId: 'dish-smash-double',
          title: 'Double Smash Burger',
          summary: 'Double patty · no pickles · + Extra cheese',
          image: IMG.burger,
          unitPrice: 52,
          qty: 1,
        },
        {
          listingId: 'dish-truffle-fries',
          title: 'Truffle Parmesan Fries',
          summary: 'Regular',
          image: IMG.burger,
          unitPrice: 24,
          qty: 1,
        },
      ],
      total: 76,
      payment: 'Paid by card',
      fulfilment: 'Delivery to Dubai Marina · arriving in about 20 min',
    },
    {
      id: 'TL-20391',
      merchant: 'EasyLease',
      placedAt: now - 1 * DAY - 3 * HOUR,
      status: 'received',
      lines: [
        {
          listingId: 'car-prado-2026',
          title: 'Toyota Prado 2026',
          summary: '3 days · Airport delivery · Full insurance',
          image: IMG.prado,
          unitPrice: 1350,
          qty: 1,
        },
      ],
      total: 1350,
      payment: 'Cash on delivery',
      fulfilment: 'Handover at DXB Terminal 3 · Sat, 10:00',
    },
    {
      id: 'TL-20207',
      merchant: 'Maison Aroma',
      placedAt: now - 6 * DAY,
      progressAt: now - 6 * DAY + 2 * HOUR,
      completedAt: now - 5 * DAY,
      status: 'completed',
      lines: [
        {
          listingId: 'perfume-oud-noir',
          title: 'Oud Noir Eau de Parfum',
          summary: '100 ml · Gift wrapped',
          image: IMG.perfume,
          unitPrice: 420,
          qty: 1,
        },
      ],
      total: 420,
      payment: 'Paid by card',
      fulfilment: 'Delivered to Jumeirah Lake Towers',
    },
    {
      id: 'TL-20115',
      merchant: 'Linen & Loom',
      placedAt: now - 12 * DAY,
      progressAt: now - 12 * DAY + 5 * HOUR,
      completedAt: now - 10 * DAY,
      status: 'completed',
      lines: [
        {
          listingId: 'apparel-linen-shirt',
          title: 'Relaxed Linen Shirt',
          summary: 'Size M · Sand',
          image: IMG.shirt,
          unitPrice: 245,
          qty: 2,
        },
      ],
      total: 490,
      payment: 'Paid by Apple Pay',
      fulfilment: 'Delivered to Dubai Marina',
    },
    {
      id: 'TL-19984',
      merchant: 'Casa Forma',
      placedAt: now - 19 * DAY,
      cancelledAt: now - 18 * DAY,
      status: 'cancelled',
      cancelReason: 'Cancelled by you · refunded to your card',
      lines: [
        {
          listingId: 'furniture-lumen-sofa',
          title: 'Lumen 3-Seater Sofa',
          summary: 'Sand boucle',
          image: IMG.sofa,
          unitPrice: 4200,
          qty: 1,
        },
      ],
      total: 4200,
      payment: 'Refunded',
      fulfilment: 'Was due for delivery in 5 days',
    },
  ]
}
